import * as Enumerable from "linq";
import { Guid } from "../../../utilities/guid";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { bindable, Disposable } from "aurelia-framework";
import { autoinject } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { ListDisplayHelper } from "../../../helpers/list-display-helper";
import { Messages } from "../../../configuration/event-messages.constants";
import { QueryFilter, DataType } from "../../../models/queries/queries.models";


@autoinject
export class ManyToManyFilterSelector {

    @bindable
    readonly: boolean;

    @bindable
    public selectedIds: number[] = [];

    @bindable
    public propertyName: string = "";

    @bindable
    public items: Many2ManyFilterItem[] = [];
    
    @bindable
    public isMultipleProperty: boolean = true;

    @bindable
    public showActionButtons: boolean = true;
    
    @bindable
    public mobileDescription: string;

    selectAll: boolean;
    public isApplied: boolean = false;

    selectAllChanged(value: boolean) {
        if (value) {
            this.items.forEach(x => x.isSelected = true);
        }
        else {
            this.items.forEach(x => x.isSelected = false);
        }
        this.setSelected();
        this.updateDescription();
    }

    constructor(private element: Element, private eventAggregator: EventAggregator) { }

    attached() {
        setTimeout(() => {
            this.isOpen = true;
            this.setSelected();
            this.previousItems = CloneHelper.cloneObject(this.items);
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));
        });
    }

    private setSelected() {
        this.selectedIds = this.items.filter(x => x.isSelected).map(x => x.id)

        this.selectAll = this.items.filter(x => !x.isSelected).length != 0? false : true 
 
        this.updateDescription();
    }

    public clear() {
        this.isApplied = false;
        this.selectAll = false;
        this.items.forEach(x => x.isSelected = false);
        this.previousItems.forEach(x => x.isSelected = false);
        this.setSelected();
        setTimeout(() => {
            this.applyFilters(false, false);
        }, 120);
    }

    public previousItems: Many2ManyFilterItem[] = [];

    roleSelectionChanged(item: Many2ManyFilterItem) {
        this.items[this.items.indexOf(item)].isSelected = !item.isSelected;
        
        this.setSelected();
        this.updateDescription();
    }

    generateFilters() {
        const filters = [];
        if(this.selectedIds && this.selectedIds.length > 0)
        {
            if(this.isMultipleProperty)
            {
                const guid = Guid.generateGuid();
                this.items.filter(x => x.isSelected).forEach(item => {
                    let query: QueryFilter = null;
                    if(item.dataType == DataType.Boolean)
                    {
                        query = QueryFilter.getQuery(item.propertyName, true, 'eq', DataType.Boolean, filters.length > 0? false: true, false, guid);
                    }
                    else 
                    {
                        query = QueryFilter.getQuery(item.propertyName, 0, 'gt', DataType.Number, filters.length > 0? false: true, false, guid);
                    }
                    filters.push(query);
                });
            }
            else
            {
                const guid = Guid.generateGuid();
                this.items.filter(x => x.isSelected).forEach(item => {
                    const query = QueryFilter.getQuery(this.propertyName, item.id, "contains({0}, '{1}')", DataType.Default, filters.length > 0? false: true, false, guid, null, true);
                    filters.push(query);
                });
            }
        }

        return filters;
    }

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if (reset) 
        {
            this.items = this.previousItems;
            this.setSelected()
        }
        this.updateDescription();

        this.isApplied = Enumerable.from(this.items).any(x => x.isSelected);
        const filters = this.generateFilters();
        const changeEvent: CustomEvent = new CustomEvent("apply-filters",
            {
                bubbles: true,
                detail: {
                    closeFilter: closeFilter,
                    appliedFilters: filters
                }
            });
        this.element.dispatchEvent(changeEvent);
        return true;
    }

    _subscriptionToDocumentClickEvent: Disposable;

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    isOpen: boolean;
    closePopup(x: any) {
        if (x && x.source == 'document' && this.isOpen) {
            this.isOpen = false;
            this.applyFilters(true, true);
        }
    }

    updateDescription() {
        if (this.selectedIds && this.selectedIds.length > 0) {
            this.mobileDescription = ListDisplayHelper.getListToStringFromIds(this.items, this.selectedIds);
        }
        else {
            this.mobileDescription = "";
        }
    }

}

export class Many2ManyFilterItem {
    name: string;
    id: number;
    propertyName?: string;
    dataType?: DataType = DataType.Number;
    isSelected?: boolean = false;

    constructor(id: number, name: string, propertyName: string = null, dataType: DataType = DataType.Number) {
        this.id = id;
        this.name = name;
        this.propertyName = propertyName;
        this.dataType = dataType;
    }
}
